/**
 * Push Notification Scheduled List Component
 * Displays and manages scheduled notifications
 */
import { Clock, Send, Play, Pause, Edit, Trash2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-FJ', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getScheduleDescription = (notif) => {
  const time = notif.schedule_time;
  switch (notif.schedule_type) {
    case 'once':
      return `Once at ${time}${notif.start_date ? ` on ${notif.start_date}` : ''}`;
    case 'daily':
      return `Daily at ${time}`;
    case 'weekly':
      const days = (notif.schedule_days || [])
        .map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label?.slice(0, 3))
        .filter(Boolean)
        .join(', ');
      return `Weekly on ${days} at ${time}`;
    case 'custom':
      return `Monthly on days ${(notif.schedule_days || []).join(', ')} at ${time}`;
    default:
      return time;
  }
};

export default function ScheduledNotificationsList({ 
  notifications, 
  onRefresh, 
  onEdit,
  onNewSchedule 
}) {
  
  const handleToggle = async (id) => {
    try {
      const response = await api.post(`/push/scheduled/${id}/toggle`);
      
      if (response.data.success) {
        toast.success(response.data.is_active ? 'Schedule activated' : 'Schedule paused');
        onRefresh?.();
      }
    } catch (error) {
      toast.error('Failed to toggle schedule');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this scheduled notification?')) return;
    
    try {
      const response = await api.delete(`/push/scheduled/${id}`);
      
      if (response.data.success) {
        toast.success('Schedule deleted');
        onRefresh?.();
      }
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleSendNow = async (id) => {
    try {
      const response = await api.post(`/push/scheduled/${id}/send-now`);
      
      if (response.data.success) {
        toast.success(`Sent to ${response.data.sent} subscribers!`);
        onRefresh?.();
      } else {
        toast.error(response.data.error || 'Failed to send');
      }
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-foreground">Scheduled Notifications</CardTitle>
          <CardDescription>Automated recurring push notifications</CardDescription>
        </div>
        <Button size="sm" onClick={onNewSchedule} data-testid="new-scheduled-btn">
          <Calendar className="w-4 h-4 mr-2" />
          New Schedule
        </Button>
      </CardHeader>
      <CardContent>
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No scheduled notifications yet</p>
            <p className="text-sm">Click "New Schedule" to create automated notifications</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notif) => (
              <div 
                key={notif.id}
                className={`p-4 rounded-lg border ${notif.is_active ? 'border-green-500/30 bg-green-500/5' : 'border-border bg-muted/20'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground truncate">{notif.title}</h4>
                      <Badge variant={notif.is_active ? "default" : "secondary"} className="shrink-0">
                        {notif.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{notif.body}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getScheduleDescription(notif)}
                      </span>
                      {notif.send_count > 0 && (
                        <span className="flex items-center gap-1">
                          <Send className="w-3 h-3" />
                          Sent {notif.send_count}x
                        </span>
                      )}
                      {notif.last_sent && (
                        <span>Last: {formatDate(notif.last_sent)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleSendNow(notif.id)}
                      title="Send Now"
                      data-testid={`send-now-${notif.id}`}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleToggle(notif.id)}
                      title={notif.is_active ? 'Pause' : 'Activate'}
                      data-testid={`toggle-${notif.id}`}
                    >
                      {notif.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => onEdit?.(notif)}
                      title="Edit"
                      data-testid={`edit-${notif.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(notif.id)}
                      title="Delete"
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-${notif.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
